import React, { useState, useRef, useEffect } from 'react';
import './Customcalender.css';

const CustomCalendar = ({
  value,
  onChange,
  minDate,
  maxDate,
  showTime = false,
  dateFormat = "dd-M-yy",
  placeholder = "Select date",
  inputStyle = {},
  footerTemplate,
  showIcon = false,
  icon = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(value || new Date());
  const [selectedDate, setSelectedDate] = useState(value || null);
  const [tempSelectedDate, setTempSelectedDate] = useState(value || null);
  const [time, setTime] = useState(value ? {
    hours: value.getHours(),
    minutes: value.getMinutes(),
    ampm: value.getHours() >= 12 ? 'PM' : 'AM'
  } : { hours: 12, minutes: 0, ampm: 'AM' });
  const [view, setView] = useState('date');
  const [popupPosition, setPopupPosition] = useState('bottom');
  const [currentStep, setCurrentStep] = useState('date'); // 'date' or 'time'
  
  const calendarRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(300);

  // Today's date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // One year from today
  const oneYearFromToday = new Date(today);
  oneYearFromToday.setFullYear(today.getFullYear() + 1);

  // Min and max dates from props or defaults
  const min = minDate || today;
  const max = maxDate || oneYearFromToday;

  // Calculate available space and set popup position
  // Calculate available space and set popup position
const calculatePopupPosition = () => {
  if (!inputRef.current) return 'bottom';
  
  const inputRect = inputRef.current.getBoundingClientRect();
  const spaceBelow = window.innerHeight - inputRect.bottom;
  const spaceAbove = inputRect.top;
  const calendarHeight = showTime ? 450 : 380;
  const buffer = 20; // Add some buffer
  
  console.log('Position calculation:', {
    spaceBelow,
    spaceAbove,
    calendarHeight,
    windowHeight: window.innerHeight,
    inputBottom: inputRect.bottom
  });
  
  // If not enough space below AND enough space above, show on top
  if (spaceBelow < calendarHeight + buffer && spaceAbove > calendarHeight + buffer) {
    return 'top';
  }
  // If not enough space below AND not enough space above, reduce calendar height
  else if (spaceBelow < calendarHeight + buffer && spaceAbove < calendarHeight + buffer) {
    // Show in whichever direction has more space
    if (spaceBelow >= spaceAbove) {
      return 'bottom';
    } else {
      return 'top';
    }
  }
  
  // Default to bottom
  return 'bottom';
};

  // Update container width when component mounts or resizes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Handle calendar open
  const handleCalendarOpen = () => {
    const position = calculatePopupPosition();
    setPopupPosition(position);
    setIsOpen(!isOpen);
    setCurrentStep('date'); // Reset to date selection when opening
    // Reset temp date to current selected date when opening
    setTempSelectedDate(selectedDate);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    
    return `${day}-${month}-${year}`;
  };

  // Format time for display in AM/PM
  const formatTime = (date) => {
    if (!date) return '12:00 AM';
    
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Convert 12-hour to 24-hour
  const to24Hour = (hours, ampm) => {
    if (ampm === 'AM') {
      return hours === 12 ? 0 : hours;
    } else {
      return hours === 12 ? 12 : hours + 12;
    }
  };

  // Convert 24-hour to 12-hour
  const to12Hour = (hours) => {
    return hours % 12 || 12;
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get first day of month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Navigate to previous month
  const prevMonth = () => {
    if (view === 'date') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
    }
  };

  // Navigate to next month
  const nextMonth = () => {
    if (view === 'date') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
    }
  };

  // Switch to month view
  const switchToMonthView = () => {
    setView('month');
  };

  // Switch to date view
  const switchToDateView = () => {
    setView('date');
  };

  // Select month
  const selectMonth = (month) => {
    const newDate = new Date(currentDate.getFullYear(), month, 1);
    setCurrentDate(newDate);
    setView('date');
  };

  // Select date
  const selectDate = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setTempSelectedDate(newDate); // Store temporarily
    
    // If time selection is enabled, don't close calendar yet
    if (showTime) {
      // Apply current time to the selected date
      const hours24 = to24Hour(time.hours, time.ampm);
      newDate.setHours(hours24, time.minutes);
      setTempSelectedDate(newDate);
    } else {
      // If no time selection, update the selected date immediately
      setSelectedDate(newDate);
      if (onChange) {
        onChange({ value: newDate });
      }
      // Don't close calendar - let user click outside or OK
    }
  };

  // Handle time change
  const handleTimeChange = (type, value) => {
    let newValue = parseInt(value) || 0;
    
    if (type === 'hours') {
      newValue = Math.max(1, Math.min(12, newValue));
    } else if (type === 'minutes') {
      newValue = Math.max(0, Math.min(59, newValue));
    }
    
    setTime({ ...time, [type]: newValue });
    
    // Update temp date with new time if a date is already selected
    if (tempSelectedDate) {
      const updatedDate = new Date(tempSelectedDate);
      const hours24 = to24Hour(
        type === 'hours' ? newValue : time.hours,
        type === 'ampm' ? value : time.ampm
      );
      const minutes = type === 'minutes' ? newValue : time.minutes;
      updatedDate.setHours(hours24, minutes);
      setTempSelectedDate(updatedDate);
    }
  };

  // Handle AM/PM toggle
  const handleAmPmChange = (newAmpm) => {
    setTime({ ...time, ampm: newAmpm });
    
    // Update temp date with new AM/PM if a date is already selected
    if (tempSelectedDate) {
      const updatedDate = new Date(tempSelectedDate);
      const hours24 = to24Hour(time.hours, newAmpm);
      updatedDate.setHours(hours24, time.minutes);
      setTempSelectedDate(updatedDate);
    }
  };

  // Handle OK button click in date selection
  const handleDateOk = () => {
    if (tempSelectedDate) {
      // Apply the temp date to the actual selected date
      setSelectedDate(tempSelectedDate);
      if (onChange) {
        onChange({ value: tempSelectedDate });
      }
      
      if (showTime) {
        // Move to time selection step
        setCurrentStep('time');
      } else {
        // Close calendar if no time selection needed
        setIsOpen(false);
      }
    }
  };

  // Handle OK button click in time selection
  const handleTimeOk = () => {
    if (tempSelectedDate) {
      // Apply the selected time to the date
      const hours24 = to24Hour(time.hours, time.ampm);
      const finalDate = new Date(tempSelectedDate);
      finalDate.setHours(hours24, time.minutes);
      
      setSelectedDate(finalDate);
      
      if (onChange) {
        onChange({ value: finalDate });
      }
      
      // Close calendar
      setIsOpen(false);
    }
  };

  // Handle Back button click
  const handleBack = () => {
    if (currentStep === 'time') {
      setCurrentStep('date');
    } else {
      setIsOpen(false);
    }
  };

  // Clear all selected date and time
  const clearAll = () => {
    setSelectedDate(null);
    setTempSelectedDate(null);
    setTime({ hours: 12, minutes: 0, ampm: 'AM' });
    
    if (onChange) {
      onChange({ value: null });
    }
    
    setIsOpen(false);
  };

  // Check if date is disabled
  const isDateDisabled = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return date < min || date > max;
  };

  // Check if date is today
  const isToday = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected (temp selection)
  const isSelected = (day) => {
    if (!tempSelectedDate) return false;
    return (
      day === tempSelectedDate.getDate() &&
      currentDate.getMonth() === tempSelectedDate.getMonth() &&
      currentDate.getFullYear() === tempSelectedDate.getFullYear()
    );
  };

  // Check if month is disabled
  const isMonthDisabled = (month) => {
    const firstDay = new Date(currentDate.getFullYear(), month, 1);
    const lastDay = new Date(currentDate.getFullYear(), month + 1, 0);
    return lastDay < min || firstDay > max;
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        
        // When clicking outside, if we have a temp selected date, apply it
        if (tempSelectedDate && !selectedDate) {
          setSelectedDate(tempSelectedDate);
          if (onChange) {
            onChange({ value: tempSelectedDate });
          }
        } else if (tempSelectedDate && selectedDate && tempSelectedDate.getTime() !== selectedDate.getTime()) {
          // If temp date is different from selected date, update it
          setSelectedDate(tempSelectedDate);
          if (onChange) {
            onChange({ value: tempSelectedDate });
          }
        }
        
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tempSelectedDate, selectedDate]);

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setTempSelectedDate(value);
      if (showTime) {
        const hours = value.getHours();
        setTime({
          hours: to12Hour(hours),
          minutes: value.getMinutes(),
          ampm: hours >= 12 ? 'PM' : 'AM'
        });
      }
    } else {
      setSelectedDate(null);
      setTempSelectedDate(null);
      setTime({ hours: 12, minutes: 0, ampm: 'AM' });
    }
  }, [value, showTime]);

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <div key={`prev-${i}`} className="calendar-day other-month">
          {prevMonthDays - i}
        </div>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDateDisabled(day);
      const isTodayClass = isToday(day) ? 'today' : '';
      const isSelectedClass = isSelected(day) ? 'selected' : '';
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isTodayClass} ${isSelectedClass} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && selectDate(day)}
        >
          {day}
        </div>
      );
    }

    // Next month days
    const totalCells = 42;
    const nextMonthDays = totalCells - days.length;
    for (let day = 1; day <= nextMonthDays; day++) {
      days.push(
        <div key={`next-${day}`} className="calendar-day other-month">
          {day}
        </div>
      );
    }

    return days;
  };

  // Generate month grid
  const generateMonthGrid = () => {
    const months = [
      ['Jan', 'Feb', 'Mar', 'Apr'],
      ['May', 'Jun', 'Jul', 'Aug'],
      ['Sep', 'Oct', 'Nov', 'Dec']
    ];

    return months.map((row, rowIndex) => (
      <div key={rowIndex} className="month-row">
        {row.map((month, monthIndex) => {
          const monthNumber = rowIndex * 4 + monthIndex;
          const disabled = isMonthDisabled(monthNumber);
          
          return (
            <div
              key={month}
              className={`month-cell ${disabled ? 'disabled' : ''} ${
                currentDate.getMonth() === monthNumber ? 'selected' : ''
              }`}
              onClick={() => !disabled && selectMonth(monthNumber)}
            >
              {month}
            </div>
          );
        })}
      </div>
    ));
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Date selection footer
  const dateSelectionFooter = () => (
    <div className="footer-buttons">
      <button 
        className="ampm-button"
        onClick={handleBack}
      >
        Back
      </button>
      <button 
        className="ampm-button"
        onClick={handleDateOk}
        disabled={!tempSelectedDate}
      >
        OK
      </button>
    </div>
  );

  // Time selection footer
  const timeSelectionFooter = () => (
    <div className="footer-buttons">
      <button 
        className="ampm-button"
        onClick={handleBack}
      >
        Back
      </button>
      <button 
        className="ampm-button"
        onClick={handleTimeOk}
      >
        OK
      </button>
    </div>
  );

  return (
    <div className="custom-calendar-container" ref={containerRef}>
      <div 
        ref={inputRef}
        className={`custom-calendar-input ${selectedDate ? 'has-value' : 'no-value'}`}
        style={inputStyle}
        onClick={handleCalendarOpen}
      >
        <i style={{fontSize:"20px"}} className="pi pi-calendar calendar-icon"></i>
        <span className={`calendar-value ${selectedDate ? 'selected' : ''}`}>
          {selectedDate ? formatDate(selectedDate) + (showTime ? ` ${formatTime(selectedDate)}` : '') : placeholder}
        </span>
      </div>

      {isOpen && (
        <div 
          className={`custom-calendar-popup ${popupPosition === 'top' ? 'popup-top' : 'popup-bottom'}`}
          ref={calendarRef}
          style={{ width: `${containerWidth}px` }}
        >
          {/* Header with navigation */}
          <div className="calendar-header">
            <button className="nav-button" onClick={prevMonth}>&lt;</button>
            <div 
              className="current-month"
              onClick={view === 'date' ? switchToMonthView : switchToDateView}
              style={{ cursor: 'pointer' }}
            >
              {view === 'date' 
                ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                : `${currentDate.getFullYear()}`
              }
            </div>
            <button className="nav-button" onClick={nextMonth}>&gt;</button>
          </div>

          {/* Date Selection View */}
          {currentStep === 'date' && (
            <>
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>

              <div className="calendar-days">
                {generateCalendarDays()}
              </div>

              <div className="calendar-footer">
                {dateSelectionFooter()}
              </div>
            </>
          )}

          {/* Time Selection View */}
          {currentStep === 'time' && showTime && (
            <div className="time-selection-view">
              <div className="time-picker">
                <div className="time-input-group">
                  <label>Hours:</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={time.hours}
                    onChange={(e) => handleTimeChange('hours', e.target.value)}
                  />
                </div>
                <div className="time-input-group">
                  <label>Minutes:</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={time.minutes}
                    onChange={(e) => handleTimeChange('minutes', e.target.value)}
                  />
                </div>
                <div className="time-input-group ampm-group">
                  <label>AM/PM:</label>
                  <div className="ampm-buttons">
                    <button
                      type="button"
                      className={`ampm-button ${time.ampm === 'AM' ? 'active' : ''}`}
                      onClick={() => handleAmPmChange('AM')}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      className={`ampm-button ${time.ampm === 'PM' ? 'active' : ''}`}
                      onClick={() => handleAmPmChange('PM')}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="calendar-footer">
                {timeSelectionFooter()}
              </div>
            </div>
          )}

          {/* Month View */}
          {view === 'month' && (
            <div className="month-grid">
              {generateMonthGrid()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomCalendar;